import { useState } from 'react'
import { t, Lang, DictKey } from './i18n'
import { AddJugadorModal } from './AddJugadorModal'

// Issue #80 (club, 2026-10-22): "añade tres botones en asistencias: + Jugador, + Entrenador y
// + Delegado. Dentro de la ficha de cada persona… permite seleccionar y cambiar el rol entre jugador,
// entrenador o delegado, sin tener que eliminarla y volver a añadirla". El rol es el `grupo` del
// miembro (con_ficha / sin_ficha = jugador). Componentes compartidos por Eskola/F7 y F11.
export type Grupo = 'con_ficha' | 'sin_ficha' | 'entrenador' | 'delegado'

const GRUPO_LABEL: Record<Grupo, DictKey> = {
  con_ficha: 'grupoConFicha',
  sin_ficha: 'grupoSinFicha',
  entrenador: 'grupoEntrenador',
  delegado: 'grupoDelegado',
}

export function grupoLabel(grupo: Grupo, lang: Lang): string {
  return t(GRUPO_LABEL[grupo], lang)
}

const BOTONES: { grupo: Grupo; key: DictKey }[] = [
  { grupo: 'con_ficha', key: 'anadirJugador' },
  { grupo: 'entrenador', key: 'anadirEntrenador' },
  { grupo: 'delegado', key: 'anadirDelegado' },
]

// «+ Jugador», «+ Entrenador», «+ Delegado» y el formulario de nombre. Los entrenadores y delegados no
// salían en la tabla de asistencia de jugadores; desde #84 solo los delegados, y se avisa de dónde quedan.
export function AnadirMiembroBotones({
  lang,
  onAdd,
}: {
  lang: Lang
  onAdd: (nombre: string, grupo: Grupo) => Promise<void>
}) {
  const [grupo, setGrupo] = useState<Grupo | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)

  return (
    <>
      {BOTONES.map((b) => (
        <button key={b.grupo} onClick={() => setGrupo(b.grupo)}>
          {t(b.key, lang)}
        </button>
      ))}
      {aviso && (
        <span role="status" className="pill pill-success">
          {aviso}
        </span>
      )}
      {grupo && (
        <AddJugadorModal
          lang={lang}
          titulo={`${t('nuevoMiembro', lang)} — ${grupoLabel(grupo, lang)}`}
          onClose={() => setGrupo(null)}
          onAdd={async (nombre) => {
            await onAdd(nombre, grupo)
            setGrupo(null)
            setAviso(
              // Issue #84: los entrenadores ya salen en la tabla de asistencia; solo los delegados no.
              grupo !== 'delegado'
                ? null
                : t('anadidoPlantilla', lang).replace('{nombre}', nombre).replace('{rol}', grupoLabel(grupo, lang)),
            )
          }}
        />
      )}
    </>
  )
}

// Selector de rol de la ficha: cambia el `grupo` del miembro (misma persona, mismo historial).
export function RolSelector({
  grupo,
  lang,
  disabled,
  onChange,
}: {
  grupo: Grupo
  lang: Lang
  disabled?: boolean
  onChange: (grupo: Grupo) => Promise<void>
}) {
  return (
    <label>
      {t('tipoMiembro', lang)}
      <select value={grupo} disabled={disabled} onChange={(e) => onChange(e.target.value as Grupo)}>
        {(Object.keys(GRUPO_LABEL) as Grupo[]).map((g) => (
          <option key={g} value={g}>
            {grupoLabel(g, lang)}
          </option>
        ))}
      </select>
      {grupo === 'delegado' && <small>{t('rolDelegadoNota', lang)}</small>}
      {grupo === 'entrenador' && <small>{t('rolEntrenadorNota', lang)}</small>}
    </label>
  )
}
